import { useRef, useState, useEffect } from "react";
import ImageUploader from "./ImageUploader";
import EmojiPickerButton from "./EmojiPickerButton";
import { api } from "../lib/api";
import { Image, Video, Link as LinkIcon, Radio } from "lucide-react";
import { toast } from "sonner";

export default function PostComposer({ onPosted, defaultType = "TEXT", autoFocus = false }) {
  const [content, setContent] = useState("");
  const [type, setType] = useState(defaultType);
  const [mediaUrl, setMediaUrl] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const textRef = useRef(null);

  // Auto-scroll into view + focus when explicitly requested (e.g. ?composer=video)
  useEffect(() => {
    if (autoFocus && typeof window !== "undefined") {
      setTimeout(() => {
        textRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        textRef.current?.focus();
      }, 100);
    }
  }, [autoFocus]);

  const insertEmoji = (emoji) => {
    const el = textRef.current;
    if (!el) { setContent((c) => c + emoji); return; }
    const start = el.selectionStart ?? content.length;
    const end = el.selectionEnd ?? content.length;
    const next = content.slice(0, start) + emoji + content.slice(end);
    setContent(next);
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(start + emoji.length, start + emoji.length);
    }, 0);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!content.trim() && !mediaUrl && !linkUrl) return;
    try {
      const { data } = await api.post("/posts", { content, mediaUrl, linkUrl, type });
      onPosted?.(data);
      setContent(""); setMediaUrl(""); setLinkUrl(""); setType("TEXT");
      toast.success("Posted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <form onSubmit={submit} className="parchment p-6" data-testid="post-composer">
      <div className="relative">
        <textarea ref={textRef} value={content} onChange={(e) => setContent(e.target.value)}
          placeholder="Share a divination, ritual, teaching, or sacred reflection..."
          rows={3}
          className="w-full bg-input/50 border border-secondary/20 rounded-sm p-3 pr-12 text-foreground focus:outline-none focus:border-secondary"
          data-testid="composer-textarea" />
        <div className="absolute bottom-2 right-2">
          <EmojiPickerButton testid="composer-emoji-btn" onEmojiClick={insertEmoji} />
        </div>
      </div>
      {type === "IMAGE" && (
        <div className="mt-2">
          <ImageUploader value={mediaUrl} onChange={setMediaUrl} testid="composer-image-upload" label="Upload Image" />
        </div>
      )}
      {type === "VIDEO" && (
        <div className="mt-2">
          <ImageUploader value={mediaUrl} onChange={setMediaUrl} testid="composer-video-upload" label="Upload Video (up to 100MB)" kind="video" />
        </div>
      )}
      {type === "LINK" && (
        <input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)}
          placeholder="https://..."
          className="w-full bg-input/50 border border-secondary/20 rounded-sm p-2 mt-2 text-sm"
          data-testid="composer-link-input" />
      )}
      <div className="flex flex-col gap-3 mt-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-0.5">
          {[["TEXT", null], ["IMAGE", Image], ["VIDEO", Video], ["LINK", LinkIcon], ["LIVE", Radio]].map(([t, Icon]) => (
            <button key={t} type="button" onClick={() => setType(t)}
              className={`px-2 py-1.5 text-[10px] sm:text-xs uppercase tracking-wider flex items-center gap-1 whitespace-nowrap ${type === t ? "text-primary border-b-2 border-primary" : "text-foreground/60 hover:text-secondary"}`}
              data-testid={`composer-type-${t.toLowerCase()}`}>
              {Icon && <Icon size={12} strokeWidth={1.25} />} {t}
            </button>
          ))}
        </div>
        <button className="bg-primary text-primary-foreground px-6 py-2 rounded-sm uppercase tracking-wider text-xs self-end sm:self-auto" data-testid="composer-submit-btn">Post</button>
      </div>
    </form>
  );
}
